import template from "./hb-mine.mjs";
import { render } from "lit-html";
import jsQR from "jsqr";

export class Scanner extends HTMLElement {
    public cam: HTMLElement;
    public canvas: HTMLCanvasElement;
    public video: HTMLVideoElement;
    public context: CanvasRenderingContext2D;
    public currentFacingMode: "environment" | "user" = "environment"; // Prefer back camera

    private animationFrameId: number | null = null;
    private frameCount = 0;
    private fps = 60; // scan approximately once per second (if running at 60hz)

    constructor() {
        super();
        render(template(null), this);

        this.cam = this.querySelector("#hb-mine-cam")!;
        this.canvas = this.cam.querySelector("canvas")!;
        this.video = this.cam.querySelector("video")!;
        this.context = this.canvas.getContext("2d", {
            willReadFrequently: true
        })!;
    }

    async initScanner() {
        try {
            // Request camera access
            // We request a higher ideal resolution to make digital zoom look better
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: this.currentFacingMode,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false
            });

            this.video.srcObject = stream;

            // ---------------------------------------------------------
            // START ZOOM LOGIC
            // ---------------------------------------------------------
            const track = stream.getVideoTracks()[0];

            // Get camera capabilities (supported on Chrome Android & iOS Safari 15+)
            // specific cast to 'any' to avoid TS errors on older definitions
            const capabilities = (track as any).getCapabilities
                ? (track as any).getCapabilities()
                : {};

            if ("zoom" in capabilities) {
                const min = capabilities.zoom.min;
                const max = capabilities.zoom.max;

                // Target 2.0x zoom.
                // We clamp it between min and max to avoid errors.
                // 2.0 is usually the sweet spot for QR scanning distance.
                const targetZoom = Math.max(min, Math.min(max, 2.0));

                try {
                    await track.applyConstraints({
                        advanced: [{ zoom: targetZoom } as any]
                    });
                    console.log(`Zoom set to ${targetZoom}x`);
                } catch (err) {
                    console.warn(
                        "Camera supports zoom but failed to apply:",
                        err
                    );
                }
            }
            // ---------------------------------------------------------
            // END ZOOM LOGIC
            // ---------------------------------------------------------

            // Wait for metadata to be loaded to get correct video dimensions
            await new Promise<void>((resolve, reject) => {
                this.video.onloadedmetadata = () => {
                    // Set canvas dimensions to match video
                    this.canvas.width = this.video.videoWidth;
                    this.canvas.height = this.video.videoHeight;
                    resolve();
                };
                this.video.onerror = (e) =>
                    reject(
                        new Error(`Video error during metadata loading: ${e}`)
                    );
            });

            // Adjust video transform based on facingMode
            if (this.currentFacingMode === "environment")
                this.video.classList.remove("scale-x-[-1]");
            else this.video.classList.add("scale-x-[-1]");

            await this.video.play();
        } catch (error) {
            // console.error("Error accessing camera: ", error);
            this.dispatchEvent(
                new CustomEvent("qr-error", {
                    detail: "Please allow access to your camera"
                })
            );
        }
    }

    // returns null or undefined
    public getZoomCapabilities() {
        const stream = this.video.srcObject as MediaStream;
        if (!stream) return null;
        const track = stream.getVideoTracks()[0];
        return (track as any).getCapabilities
            ? (track as any).getCapabilities().zoom
            : null;
    }

    // Helper method: Call this if you add a slider <input type="range"> to your HTML
    public async setZoom(value: number) {
        const stream = this.video.srcObject as MediaStream;
        if (!stream) return;

        const track = stream.getVideoTracks()[0];
        const capabilities = (track as any).getCapabilities
            ? (track as any).getCapabilities()
            : {};

        if ("zoom" in capabilities) {
            try {
                await track.applyConstraints({
                    advanced: [{ zoom: value } as any]
                });
            } catch (e) {
                console.error("Failed to set manual zoom", e);
            }
        }
    }

    startScanner() {
        // if (!this.video.srcObject) await this.initScanner();
        this.frameCount++;

        // Draw current video frame to canvas
        this.context.drawImage(
            this.video,
            0,
            0,
            this.canvas.width,
            this.canvas.height
        );

        if (this.frameCount % this.fps === 0) {
            const preprocessedImageData = this.context.getImageData(
                0,
                0,
                this.canvas.width,
                this.canvas.height
            );

            // QR code detection with inversion attempts
            const code = jsQR(
                preprocessedImageData.data,
                preprocessedImageData.width,
                preprocessedImageData.height,
                {
                    inversionAttempts: "attemptBoth" // Try both normal and inverted
                }
            );
            if (code && code.data) {
                this.dispatchEvent(
                    new CustomEvent("qr-detected", {
                        detail: {
                            data: code.data,
                            image: this.canvas.toDataURL("image/webp") // jpeg
                        }
                    })
                );
            } else {
                this.dispatchEvent(new Event("none-detected"));
            }
        }

        // Continue scanning
        this.animationFrameId = requestAnimationFrame(
            this.startScanner.bind(this)
        );
    }

    stopScanner() {
        this.frameCount = 0; // Reset frame count

        // Cancel animation frame
        if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        // Pause the video
        if (!this.video.paused) {
            this.video.pause();
            // Detach event listeners to prevent memory leaks if re-starting
            this.video.onloadedmetadata = null;
            this.video.onerror = null;
        }

        if (this.video.srcObject) {
            (this.video.srcObject as MediaStream)
                .getTracks()
                .forEach((track) => {
                    track.stop();
                });
        }
    }
}
